// Scherm 7 — de vier grafiekkaarten boven de skill-lijst.
//
// Handgetekende SVG met het palet uit tokens.ts; geen grafiekbibliotheek, zodat
// er geen tweede ontwerptaal binnenkomt.
//
// OWNER-REVIEW 2026-08-25: "de weergaves prioriteitsmatrix, status, urgentie,
// trending zijn nog steeds niet overgenomen van de eerdere opzet. Neem de
// vormgeving zoveel als mogelijk over." Terecht — drie van de vier hadden een
// vorm die ik zelf had bedacht:
//
//   kaart              de dummy (V1)                          wat er stond
//   Priority Matrix    2x2 kwadranttegels mét tellingen        puntenwolk
//   Trending Velocity  top-8 skills als balken mét naam        histogram
//   Skill Landscape    TWEE donuts: levensfase én soort        één donut
//   Urgency Heatmap    top-8 skills, kleurstaafje in 5 trappen kruistabel
//
// Alle vier staan nu in de V1-vorm. Twee dingen wijken bewust af, allebei omdat
// de dummy dertig skills toonde en wij 1.762:
//
//  1. De vergrote matrix blijft een puntenwolk met bellen (één punt per
//     voorkomende impact/effort-combinatie). V1 labelt élke stip met een naam;
//     dat is bij 1.762 vaardigheden geen grafiek meer.
//  2. De ranglijsten zijn afgekapt op 40. De kaart zegt waarover gerekend is.
//
// En één ding neem ik NIET over: V1 filtert in Trending én Urgency alle
// Declining-skills weg (`filter(s => s.status !== "Declining")`). Dat is precies
// wat het owner-besluit van 2026-08-25 ongedaan maakt — een krimpende
// vaardigheid is een bevinding, geen rij om te verbergen. Ze staan erbij, met
// hun ↓-markering.
//
// Alle vier tellen VAARDIGHEDEN over dezelfde selectie als de lijst eronder; de
// lijst telt REGELS (een geneste skill telt daar onder zijn ouder mee). Dat
// verschil staat op de kaarten.

import { useState, type ReactNode } from "react";

import { EmptyReason } from "./Empty";
import { ExpandButton, InfoIcon, Modal, useZweefKaart } from "./ui";
import type { SkillChartsResponse } from "../contracts";
import { chart, color, radius, size, weight } from "../tokens";

type Kaart = "matrix" | "status" | "urgentie" | "trending";
type Rang = NonNullable<SkillChartsResponse["topTrending"]>[number];

/** De quick-wingrens uit config/prioriteit.yaml — zelfde regel als de badge. */
const QW_IMPACT = 8;
const QW_EFFORT = 6;

function ChartCard({
  title,
  hint,
  onExpand,
  children,
  footer,
}: {
  title: string;
  hint: string;
  onExpand: () => void;
  children: ReactNode;
  footer?: ReactNode;
}) {
  return (
    <div
      className="chart-card"
      onClick={onExpand}
      style={{
        background: color.surface,
        border: `1px solid ${color.line}`,
        borderRadius: radius.card,
        padding: 16,
        display: "flex",
        flexDirection: "column",
        gap: 10,
        minWidth: 0,
        cursor: "pointer",
      }}
    >
      <div
        style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 8 }}
      >
        {/* De dummy zet hier een echte kaarttitel (14px/700 zwart), geen
            kolomlabel in kapitalen. */}
        <span style={{ fontSize: 14, fontWeight: weight.bold, color: color.ink }}>{title}</span>
        <span style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
          <InfoIcon tooltip={hint} />
          <ExpandButton onClick={onExpand} label={`${title} vergroten`} />
        </span>
      </div>
      <div style={{ flex: 1, minHeight: 0 }}>{children}</div>
      {footer && <div style={{ fontSize: 11, color: color.faint, lineHeight: 1.5 }}>{footer}</div>}
    </div>
  );
}

/** De herkomstregel onder een vergrote kaart. */
function Herkomst({ regels }: { regels: string[] }) {
  return (
    <div
      style={{
        marginTop: 18,
        paddingTop: 12,
        borderTop: `1px solid ${color.lineFaint}`,
        display: "flex",
        flexDirection: "column",
        gap: 3,
      }}
    >
      <span
        style={{
          fontSize: 10,
          fontWeight: weight.medium,
          color: color.faint,
          letterSpacing: "0.08em",
          textTransform: "uppercase",
          marginBottom: 2,
        }}
      >
        Waar dit vandaan komt
      </span>
      {regels.map((r, i) => (
        <span key={i} style={{ fontSize: 11, color: color.faint, lineHeight: 1.55 }}>
          {r}
        </span>
      ))}
    </div>
  );
}

type Deel = { label: string; n: number; kleur: string };
type Voorbeeld = { id: string; name: string };

/**
 * Wat er achter één aggregaatbak zit — owner-review 2026-08-25: "ik zie allerlei
 * bolletjes maar dit zegt me niks; ik wil kunnen zien wat eronder schuilgaat."
 *
 * Zelfde kaartje voor de bolletjes van de matrix en de staven van het
 * histogram: kop met wat de bak is, het aantal, drie namen en hoeveel er nog
 * meer in zitten. Zonder dat laatste getal lijken die drie de hele inhoud.
 */
function BakInhoud({
  kop,
  aantal,
  voorbeelden,
}: {
  kop: string;
  aantal: number;
  voorbeelden?: Voorbeeld[];
}) {
  const rest = aantal - (voorbeelden?.length ?? 0);
  return (
    <span style={{ display: "block" }}>
      <span style={{ display: "block", fontWeight: weight.medium, marginBottom: 4 }}>
        {kop}
      </span>
      <span style={{ display: "block", marginBottom: voorbeelden?.length ? 6 : 0 }}>
        {aantal.toLocaleString("nl-NL")} {aantal === 1 ? "vaardigheid" : "vaardigheden"}
      </span>
      {voorbeelden?.map((v) => (
        <span key={v.id} style={{ display: "block", opacity: 0.85 }}>
          · {v.name}
        </span>
      ))}
      {rest > 0 && (
        <span style={{ display: "block", opacity: 0.6, marginTop: 4 }}>
          + {rest.toLocaleString("nl-NL")} andere
        </span>
      )}
    </span>
  );
}

/** Het ↓-teken bij een krimpende vaardigheid in een ranglijst. */
function Krimp({ status }: { status?: string }) {
  if (status !== "Declining") return null;
  return (
    <span
      title="Afnemend: de bronnen beschrijven krimp in de vraag naar dit werk."
      style={{ color: color.accentAmber, fontWeight: weight.bold, flexShrink: 0 }}
    >
      ↓
    </span>
  );
}

// ── A. Priority Matrix ──────────────────────────────────────────────────────
//
// Klein: de vier kwadranten als gekleurde tegels mét telling (V1 r. 934-966).
// Groot: de puntenwolk met kwadrantvlakken eronder.

/**
 * De vier vakken. Alleen "Quick wins" is aanklikbaar, en dat is geen willekeur.
 *
 * Het filtermenu werkt met BANDEN (impact: 9+ / 7-9 / 5-7 / <5; effort: <3 /
 * 3-5 / 5-7 / 7+) en de kwadrantgrens ligt op impact 8 en effort 6 — dwars door
 * twee van die banden heen. Een klik op "Strategisch" zou dus een andere
 * selectie geven dan de tegel telt; gemeten leverde die eerste poging 22 rijen
 * onder een tegel die 129 zei. Liever geen klik dan een klik die iets anders
 * doet dan hij belooft.
 *
 * Quick wins kán wel exact: daar bestaat één filter voor, met precies dezelfde
 * grens als deze tegel gebruikt.
 */
const KWADRANTEN = [
  {
    key: "qw", label: "Quick wins", fg: "#059669", bg: "#f0fdf4", klikbaar: true,
    uitleg: "Veel opbrengst, weinig moeite. Klik om precies deze vaardigheden in de lijst te zien.",
  },
  {
    key: "strat", label: "Strategisch", fg: "#b45309", bg: "#fefce8", klikbaar: false,
    uitleg: "Veel opbrengst, maar het kost tijd om te leren.",
  },
  {
    key: "laag", label: "Lage prioriteit", fg: color.faint, bg: color.surfaceQuote,
    klikbaar: false,
    uitleg: "Snel te leren, maar het levert minder op.",
  },
  {
    key: "her", label: "Heroverwegen", fg: "#dc2626", bg: "#fef2f2", klikbaar: false,
    uitleg: "Kost tijd én levert minder op.",
  },
] as const;

function kwadrantTelling(data: SkillChartsResponse["matrix"]) {
  const t = { qw: 0, strat: 0, laag: 0, her: 0 };
  for (const d of data) {
    const hoog = d.impact >= QW_IMPACT;
    const goedkoop = d.effort <= QW_EFFORT;
    if (hoog && goedkoop) t.qw += d.count;
    else if (hoog) t.strat += d.count;
    else if (goedkoop) t.laag += d.count;
    else t.her += d.count;
  }
  return t;
}

function PriorityMatrix({
  data,
  groot = false,
  onKies,
}: {
  data: SkillChartsResponse["matrix"];
  groot?: boolean;
  /** Klik op "Quick wins": zet het quick-winfilter op de lijst eronder. */
  onKies?: () => void;
}) {
  const zweef = useZweefKaart();
  if (data.length === 0) return <EmptyReason reason="geen vaardigheden met deze filters" />;
  const t = kwadrantTelling(data);

  if (!groot) {
    return (
      <div>
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
          {KWADRANTEN.map((k) => (
            <div
              key={k.key}
              title={k.uitleg}
              onClick={(e) => {
                if (!onKies || !k.klikbaar) return;
                e.stopPropagation();
                onKies();
              }}
              style={{
                background: k.bg,
                borderRadius: radius.block,
                padding: "14px 12px",
                textAlign: "center",
                cursor: onKies && k.klikbaar ? "pointer" : "default",
              }}
            >
              <div style={{ fontSize: 24, fontWeight: weight.bold, color: k.fg, lineHeight: 1 }}>
                {t[k.key].toLocaleString("nl-NL")}
              </div>
              <div
                style={{ fontSize: 11, color: k.fg, fontWeight: weight.medium, marginTop: 4 }}
              >
                {k.label}
              </div>
            </div>
          ))}
        </div>
        <div style={{ display: "flex", justifyContent: "space-between", marginTop: 6 }}>
          <span style={{ fontSize: 10, color: color.disabled }}>↑ Impact</span>
          <span style={{ fontSize: 10, color: color.disabled }}>Effort →</span>
        </div>
      </div>
    );
  }

  const W = 760;
  const H = 430;
  const pad = { l: 52, r: 20, t: 16, b: 44 };
  const x = (e: number) => pad.l + (e / 10) * (W - pad.l - pad.r);
  const y = (i: number) => H - pad.b - (i / 10) * (H - pad.t - pad.b);
  const max = Math.max(1, ...data.map((d) => d.count));
  return (
    <>
      <svg viewBox={`0 0 ${W} ${H}`} width="100%" role="img" aria-label="Impact tegen effort">
      <rect x={x(0)} y={y(10)} width={x(QW_EFFORT) - x(0)} height={y(QW_IMPACT) - y(10)}
        fill="#f0fdf4" />
      <rect x={x(QW_EFFORT)} y={y(10)} width={x(10) - x(QW_EFFORT)} height={y(QW_IMPACT) - y(10)}
        fill="#fefce8" />
      <rect x={x(0)} y={y(QW_IMPACT)} width={x(QW_EFFORT) - x(0)} height={y(0) - y(QW_IMPACT)}
        fill={color.surfaceQuote} />
      <rect x={x(QW_EFFORT)} y={y(QW_IMPACT)} width={x(10) - x(QW_EFFORT)}
        height={y(0) - y(QW_IMPACT)} fill="#fef2f2" />
      {[0, 1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((v) => (
        <g key={v}>
          <line x1={x(0)} x2={x(10)} y1={y(v)} y2={y(v)} stroke={chart.grid} strokeWidth="0.5" />
          <line x1={x(v)} x2={x(v)} y1={y(0)} y2={y(10)} stroke={chart.grid} strokeWidth="0.5" />
          <text x={pad.l - 6} y={y(v) + 4} textAnchor="end" fontSize="11" fill={color.faint}>{v}</text>
          <text x={x(v)} y={y(0) + 16} textAnchor="middle" fontSize="11" fill={color.faint}>{v}</text>
        </g>
      ))}
      {KWADRANTEN.map((k, i) => (
        <text
          key={k.key}
          x={i % 2 === 0 ? x(0.3) : x(QW_EFFORT + 0.3)}
          y={i < 2 ? y(9.6) : y(QW_IMPACT - 0.5)}
          fontSize="12"
          fontWeight={600}
          fill={k.fg}
        >
          {k.label} · {t[k.key].toLocaleString("nl-NL")}
        </text>
      ))}
      {data.map((d, i) => (
        <circle
          key={i}
          cx={x(d.effort)}
          cy={y(d.impact)}
          r={4 + Math.sqrt(d.count / max) * 12}
          fill={d.impact >= QW_IMPACT && d.effort <= QW_EFFORT ? chart.green : chart.blue}
          opacity={0.5}
          style={{ cursor: "default" }}
          onMouseEnter={(e) =>
            zweef.toon(
              e.currentTarget,
              <BakInhoud
                kop={`Impact ${d.impact.toFixed(1)} · effort ${d.effort.toFixed(1)}`}
                aantal={d.count}
                voorbeelden={d.examples}
              />,
            )
          }
          onMouseLeave={zweef.verberg}
        />
      ))}
      <text x={x(5)} y={H - 8} textAnchor="middle" fontSize="12" fill={color.faint}>
        Effort → (hoeveel investering het kost)
      </text>
      <text x={14} y={y(5)} textAnchor="middle" fontSize="12" fill={color.faint}
        transform={`rotate(-90 14 ${y(5)})`}>
        Impact →
      </text>
      </svg>
      {zweef.kaart}
    </>
  );
}

// ── B. Trending Velocity ────────────────────────────────────────────────────
//
// De ranglijst van de dummy (V1 r. 1044-1060): rangnummer, balk, score. Klein
// de eerste acht zonder naam (daar is geen ruimte), groot mét naam.

/**
 * Trending als VERDELING, niet als ranglijst — owner-besluit ronde 6.
 *
 * Waarom geen ranglijst: gemeten staan **38 van de top-40 op exact 9,5**. De
 * volgorde binnen die groep wordt gebroken op bronaantal en naam, en dat is
 * alfabet en geen rangschikking; de balken waren bovendien allemaal even lang
 * omdat ze relatief aan het maximum schaalden. Een verdeling zegt wél iets:
 * waar zit de massa.
 *
 * En omdat een staaf op zichzelf ook maar een getal is, toont hover wat erin
 * zit — dat was de andere helft van het besluit.
 */
function TrendingVerdeling({
  data,
  groot = false,
}: {
  data: SkillChartsResponse["trending"];
  groot?: boolean;
}) {
  const zweef = useZweefKaart();
  if (!data || data.length === 0) {
    return <EmptyReason reason="geen vaardigheden met deze filters" />;
  }
  // width_bucket geeft 1..10 voor de schaal 0-10; bak n dekt [n-1, n).
  const bakken = Array.from({ length: 10 }, (_, i) => {
    const b = data.find((d) => d.bucket === i + 1);
    return { van: i, tot: i + 1, n: b?.count ?? 0, examples: b?.examples };
  });
  const max = Math.max(1, ...bakken.map((b) => b.n));
  return (
    <>
      <div
        style={{
          display: "flex",
          alignItems: "flex-end",
          gap: groot ? 8 : 3,
          height: groot ? 300 : 118,
        }}
      >
        {bakken.map((b) => (
          <div
            key={b.van}
            onMouseEnter={(e) =>
              b.n > 0 &&
              zweef.toon(
                e.currentTarget,
                <BakInhoud
                  kop={`Trending ${b.van} tot ${b.tot}`}
                  aantal={b.n}
                  voorbeelden={b.examples}
                />,
              )
            }
            onMouseLeave={zweef.verberg}
            style={{
              flex: 1,
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-end",
              cursor: b.n > 0 ? "default" : "auto",
            }}
          >
            {groot && (
              <span
                style={{
                  fontSize: 11,
                  color: b.n ? color.soft : color.disabled,
                  textAlign: "center",
                  marginBottom: 4,
                }}
              >
                {b.n}
              </span>
            )}
            <div
              style={{
                height: `${(b.n / max) * 100}%`,
                minHeight: b.n ? 3 : 0,
                background: b.van >= 8 ? chart.blue : b.van >= 6 ? chart.blueL : chart.slate,
                borderRadius: "2px 2px 0 0",
                transition: "opacity 0.12s",
              }}
            />
            <span
              style={{
                fontSize: groot ? 11 : 9,
                color: color.faint,
                textAlign: "center",
                marginTop: 3,
              }}
            >
              {b.van}
            </span>
          </div>
        ))}
      </div>
      {zweef.kaart}
    </>
  );
}

// ── C. Skill Landscape — twee donuts ────────────────────────────────────────

function MiniDonut({
  parts,
  totaal,
  size: r = 30,
  sw = 6,
}: {
  parts: Deel[];
  totaal: number;
  size?: number;
  sw?: number;
}) {
  const circ = 2 * Math.PI * r;
  let offset = 0;
  const mid = r + sw / 2;
  return (
    <svg width={r * 2 + sw} height={r * 2 + sw} style={{ flexShrink: 0 }}>
      <circle cx={mid} cy={mid} r={r} fill="none" stroke={color.lineFaint} strokeWidth={sw} />
      {parts.map((p) => {
        const dash = totaal > 0 ? (p.n / totaal) * circ : 0;
        const el = (
          <circle
            key={p.label}
            cx={mid}
            cy={mid}
            r={r}
            fill="none"
            stroke={p.kleur}
            strokeWidth={sw}
            strokeDasharray={`${dash} ${circ}`}
            strokeDashoffset={-offset}
            transform={`rotate(-90 ${mid} ${mid})`}
          >
            <title>{`${p.label}: ${p.n}`}</title>
          </circle>
        );
        offset += dash;
        return el;
      })}
      <text x={mid} y={mid + 4} textAnchor="middle" fontSize={r > 40 ? 16 : 11}
        fontWeight={700} fill={color.ink}>
        {totaal.toLocaleString("nl-NL")}
      </text>
    </svg>
  );
}

function Legenda({
  parts,
  totaal,
  groot,
}: {
  parts: Deel[];
  totaal: number;
  groot: boolean;
}) {
  return (
    <div style={{ display: "flex", gap: groot ? 14 : 10, flexWrap: "wrap" }}>
      {parts.map((p) => (
        <span key={p.label} style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 6, height: 6, borderRadius: 99, background: p.kleur }} />
          <span style={{ fontSize: groot ? 13 : 11, color: color.soft }}>
            {p.n.toLocaleString("nl-NL")} {p.label}
            {groot && totaal > 0 && (
              <span style={{ color: color.disabled }}> · {((p.n / totaal) * 100).toFixed(0)}%</span>
            )}
          </span>
        </span>
      ))}
    </div>
  );
}

function SkillLandscape({
  status,
  types,
  groot = false,
}: {
  status: SkillChartsResponse["status"];
  types: SkillChartsResponse["types"];
  groot?: boolean;
}) {
  const sc = Object.fromEntries((status ?? []).map((d) => [d.status, d.count]));
  const tc = Object.fromEntries((types ?? []).map((d) => [d.type, d.count]));
  const fase: Deel[] = [
    { label: "Opkomend", n: sc.Emerging ?? 0, kleur: chart.green },
    { label: "Gevestigd", n: sc.Established ?? 0, kleur: chart.blue },
    // Amber, niet grijs: krimp is een bevinding (owner-besluit 2026-08-25).
    // De dummy kleurde deze ring #d1d5db — dezelfde "uitgeschakeld"-lezing.
    { label: "Afnemend", n: sc.Declining ?? 0, kleur: chart.amber },
  ];
  const soort: Deel[] = [
    { label: "Tool", n: tc.Tool ?? 0, kleur: "#6366f1" },
    { label: "Hard Skill", n: tc["Hard Skill"] ?? 0, kleur: chart.green },
    { label: "Soft Skill", n: tc["Soft Skill"] ?? 0, kleur: chart.amber },
    { label: "Methodology", n: tc.Methodology ?? 0, kleur: chart.slate },
  ];
  const totaal = fase.reduce((n, p) => n + p.n, 0);
  const totaalSoort = soort.reduce((n, p) => n + p.n, 0);
  if (totaal === 0) return <EmptyReason reason="geen vaardigheden met deze filters" />;

  const blok = (kop: string, parts: Deel[], tot: number) => (
    <div style={{ display: "flex", alignItems: "center", gap: groot ? 20 : 12 }}>
      <MiniDonut parts={parts} totaal={tot} size={groot ? 52 : 30} sw={groot ? 12 : 6} />
      <div style={{ flex: 1, minWidth: 0 }}>
        <div
          style={{
            fontSize: groot ? 13 : 11,
            fontWeight: weight.medium,
            color: color.soft,
            marginBottom: 4,
          }}
        >
          {kop}
        </div>
        <Legenda parts={parts} totaal={tot} groot={groot} />
      </div>
    </div>
  );
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: groot ? 28 : 14 }}>
      {blok("Levensfase", fase, totaal)}
      {blok("Soort", soort, totaalSoort)}
    </div>
  );
}

// ── D. Urgency Heatmap ──────────────────────────────────────────────────────
//
// De vijf kleurtrappen van de dummy (V1 r. 1210), skills op urgentie gerangschikt.

const trap = (v: number) =>
  v >= 8.5 ? "#991b1b" : v >= 7.5 ? "#dc2626" : v >= 6.5 ? "#f97316" : v >= 5.5 ? "#fbbf24" : color.disabled;

function UrgencyHeatmap({ data, groot = false }: { data: Rang[]; groot?: boolean }) {
  if (!data || data.length === 0) {
    return <EmptyReason reason="geen vaardigheden met deze filters" />;
  }
  const rijen = groot ? data : data.slice(0, 8);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: groot ? 8 : 4 }}>
      {rijen.map((s) => (
        <div key={s.id} style={{ display: "flex", alignItems: "center", gap: groot ? 10 : 6 }}>
          <div
            style={{
              width: groot ? 10 : 6,
              height: groot ? 26 : 14,
              borderRadius: groot ? 4 : 2,
              background: trap(s.score),
              flexShrink: 0,
            }}
          />
          <Krimp status={s.status} />
          <span
            title={s.name}
            style={{
              fontSize: groot ? 14 : 10,
              color: color.body,
              fontWeight: groot ? weight.regular : 400,
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              textOverflow: "ellipsis",
              whiteSpace: "nowrap",
            }}
          >
            {/* Klein: de eerste woorden, zoals de dummy (r. 1226). */}
            {groot ? s.name : s.name.split(" ").slice(0, 3).join(" ")}
          </span>
          <span
            style={{
              fontSize: groot ? 14 : 10,
              fontWeight: weight.bold,
              color: trap(s.score),
              flexShrink: 0,
            }}
          >
            {s.score.toFixed(1)}
          </span>
        </div>
      ))}
    </div>
  );
}

// ── de vier kaarten ─────────────────────────────────────────────────────────

/**
 * Wat er per kaart in de kop, de voet en de vergrote stand staat.
 *
 * De herkomstregels zijn geschreven voor iemand die dit platform niet kent
 * (owner-review 2026-08-25). Geen bestandsnamen, geen sessiecodes, geen interne
 * woorden als "detailpas", "rollup" of "de brug" — maar ook geen informatie
 * minder: waar een instelling achter zit, staat er dát er een instelling achter
 * zit. Vast patroon per regel: wat is geteld · waar komt het vandaan · wat kun
 * je er níét uit afleiden.
 */
const KAARTEN: Record<
  Kaart,
  { titel: string; hint: string; onder: ReactNode; sub: string; herkomst: string[] }
> = {
  matrix: {
    titel: "Prioriteitsmatrix",
    hint:
      "Hoeveel levert een vaardigheid op, en hoeveel moeite kost het om hem te " +
      "leren? Linksboven staat de winst: veel opbrengst voor weinig moeite.",
    onder: `Quick win = veel opbrengst (${QW_IMPACT}+) en weinig moeite (${QW_EFFORT} of minder). Klik dat vak om ze in de lijst te zien.`,
    sub: "Opbrengst verticaal, moeite horizontaal — linksboven ligt de winst",
    herkomst: [
      "Geteld: alle vaardigheden die op dit moment in de lijst hieronder staan. Verander je een filter, dan verandert deze kaart mee; blader je naar de volgende pagina, dan niet.",
      "De twee cijfers komen uit de leesronde: per vaardigheid heeft het systeem beoordeeld hoe breed hij het werk raakt (opbrengst) en hoeveel er nodig is om hem te leren (moeite). Allebei op een schaal van 1 tot 10.",
      "De grens tussen de vier vakken — opbrengst 8 en moeite 6 — is een instelling die je kunt aanpassen. Het is dezelfde grens die het label 'quick win' in de lijst gebruikt, dus het vak en het label tellen altijd hetzelfde aantal.",
      "Elk bolletje is één combinatie van opbrengst en moeite; hoe dikker, hoe meer vaardigheden erop liggen. Beweeg erover om te zien welke dat zijn.",
      "Alleen het vak 'Quick wins' is aanklikbaar. Voor de andere drie bestaat geen filter dat exact dezelfde grens gebruikt, en een knop die iets anders selecteert dan de tegel telt is misleidend.",
      "Wat je hier NIET uit kunt afleiden: of een vaardigheid urgent is. Dat staat op de kaart ernaast.",
    ],
  },
  status: {
    titel: "Skill-landschap",
    hint:
      "Twee verdelingen: waar de vaardigheden in hun levensloop staan, en wat " +
      "voor soort vaardigheid het is.",
    onder:
      "Telt vaardigheden. Het menu 'Status' in de filterbalk telt regels in de " +
      "lijst en komt daardoor hoger uit.",
    sub: "Levensfase en soort, over dezelfde selectie",
    herkomst: [
      "Geteld: vaardigheden, ieder één keer. Het filtermenu 'Status' telt iets anders — daar telt een vaardigheid die onder een andere hangt zowel apart als bij zijn bovenliggende mee. Vandaar dat die getallen hoger zijn.",
      "De levensfase komt uit wat de bronnen zeggen: is dit werk nieuw en groeiend, al breed gevraagd, of aan het verdwijnen? Waar de bronnen elkaar tegenspreken, wint wat het vaakst gezegd wordt.",
      "'Afnemend' staat er in oranje en niet in grijs: dat werk verdwijnt, en dat is nieuws — geen voetnoot. Er zijn er 26.",
      "Wat je hier NIET uit kunt afleiden: hoe belangrijk een vaardigheid is. Een gevestigde vaardigheid kan even hard nodig zijn als een opkomende.",
    ],
  },
  urgentie: {
    titel: "Urgentie",
    hint: "Bij welke vaardigheden de klok het hardst tikt. Donkerder is meer haast.",
    onder:
      "De acht urgentste in deze selectie. Let op: bijna alle vaardigheden " +
      "bovenaan hebben hetzelfde cijfer — de volgorde daartussen zegt niets.",
    sub: "Vaardigheden op urgentie — donkerder is meer haast",
    herkomst: [
      "Geteld: alle vaardigheden in de lijst hieronder, gerangschikt op hoe snel het speelt.",
      "Het urgentiecijfer komt uit de leesronde: het systeem beoordeelt per vaardigheid of er een deadline, een wet of een marktdruk achter zit.",
      "BELANGRIJK: van de veertig hoogste staan er 39 op precies 9,5. Het cijfer maakt bovenin dus geen verschil meer, en de volgorde daarbinnen is willekeurig. Lees deze lijst als 'dit is de urgente groep', niet als een ranglijst van eerste tot laatste.",
      "De kleuren zijn vaste stappen: donkerrood vanaf 8,5, dan rood, oranje, geel en grijs.",
    ],
  },
  trending: {
    titel: "Trending",
    hint:
      "Waar zit de massa? Hoe hoger het cijfer, hoe meer er over die " +
      "vaardigheden geschreven wordt. Beweeg over een staaf om te zien welke.",
    onder: "Beweeg over een staaf: dan zie je hoeveel vaardigheden erin zitten en welke.",
    sub: "Verdeling over de schaal — beweeg over een staaf voor de inhoud",
    herkomst: [
      "Geteld: alle vaardigheden in de lijst hieronder, verdeeld over de schaal van 1 tot 10.",
      "Het cijfer zegt hoe hard er op dit moment over een vaardigheid geschreven wordt in de bronnen — niet hoe belangrijk hij is.",
      "Dit was eerst een lijstje van de acht hoogste. Dat is vervangen omdat het geen ranglijst bleek: 38 van de veertig hoogste staan op precies 9,5, dus de volgorde daartussen was willekeurig. Een verdeling laat wél iets zien, namelijk waar de meeste vaardigheden zitten.",
      "Dit is een momentopname, geen verloop. Zodra er meer metingen zijn, kan hier een ontwikkeling in de tijd komen te staan.",
    ],
  },
};

export function SkillCharts({
  data,
  loading,
  onKiesKwadrant,
}: {
  data: SkillChartsResponse | null;
  loading: boolean;
  /** Klik op de quick-wintegel: zet het quick-winfilter op de lijst. */
  onKiesKwadrant?: () => void;
}) {
  const [groot, setGroot] = useState<Kaart | null>(null);

  if (loading || !data) {
    return (
      <div
        style={{
          background: color.surface,
          border: `1px solid ${color.line}`,
          borderRadius: radius.card,
          padding: 24,
          marginBottom: 16,
          textAlign: "center",
          color: color.faint,
          fontSize: size.meta,
        }}
      >
        grafieken laden…
      </div>
    );
  }

  const teken = (kaart: Kaart, groter: boolean) => {
    if (kaart === "matrix") {
      return (
        <PriorityMatrix data={data.matrix} groot={groter} onKies={onKiesKwadrant} />
      );
    }
    if (kaart === "status") {
      return <SkillLandscape status={data.status} types={data.types ?? []} groot={groter} />;
    }
    if (kaart === "urgentie") {
      return <UrgencyHeatmap data={data.topUrgentie ?? []} groot={groter} />;
    }
    return <TrendingVerdeling data={data.trending} groot={groter} />;
  };

  return (
    <>
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(270px, 1fr))",
          gap: 12,
          marginBottom: 16,
        }}
      >
        {(Object.keys(KAARTEN) as Kaart[]).map((kaart) => (
          <ChartCard
            key={kaart}
            title={KAARTEN[kaart].titel}
            hint={KAARTEN[kaart].hint}
            footer={KAARTEN[kaart].onder}
            onExpand={() => setGroot(kaart)}
          >
            {teken(kaart, false)}
          </ChartCard>
        ))}
      </div>

      <Modal
        open={groot !== null}
        title={groot ? KAARTEN[groot].titel : ""}
        subtitle={groot ? KAARTEN[groot].sub : undefined}
        onClose={() => setGroot(null)}
      >
        {groot && (
          <>
            {teken(groot, true)}
            <Herkomst regels={KAARTEN[groot].herkomst} />
          </>
        )}
      </Modal>
    </>
  );
}
